import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Answer } from '@/components/common/Answer';
import type { AnswerOut, CvmFilterOption, CvmPersonScope } from '@/services/types';

/**
 * The pieces every CVM page shares, in one file rather than fifteen copies.
 *
 * `PersonScopeCard` is the disclosure the 2026-09-19 decision requires on every
 * screen that can print a party name: accused are shown as CVM itself publishes
 * them, the person/company split comes from a name-shape rule the loader
 * records, and nothing sums or ranks a party across cases. The backend answers
 * `person_scope` with the measured split and names the same locale key a
 * refusal would; the card renders that key with the live figures, so the
 * sentence cannot go stale the way a string in a locale file does.
 */
export function PersonScopeCard({ answer }: { answer: AnswerOut<CvmPersonScope> }) {
  const { t, i18n } = useTranslation(['cvm', 'common']);
  const number = new Intl.NumberFormat(i18n.language);
  const percent = new Intl.NumberFormat(i18n.language, {
    style: 'percent',
    maximumFractionDigits: 1,
  });
  return (
    <section
      className="rounded-lg border border-amber-500/40 bg-card p-4"
      data-person-scope
      aria-label={t('cvm:personScope.title')}
    >
      <h2 className="mb-2 text-sm font-semibold text-foreground">{t('cvm:personScope.title')}</h2>
      <Answer answer={answer} help={t('cvm:personScope.help')}>
        {(scope) => (
          <div className="space-y-2 text-xs text-muted-foreground">
            {/* `Answer` prints the backend's reason_key itself when the answer
                is PARTIAL (the name-shape rule left more than one party in
                twenty undecided); rendering it here too showed the sentence
                twice. When coverage is high enough for `available`, Answer
                prints nothing and the disclosure must still be on screen. */}
            {answer.substrate === 'partial' ? null : (
              <p>
                {t('common:substrate.reasons.cvmNamedAsPublished', {
                  parties: scope.parties,
                  persons: scope.persons,
                  companies: scope.companies,
                })}
              </p>
            )}
            <dl className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              <Figure label={t('cvm:personScope.parties')} value={number.format(scope.parties)} />
              <Figure
                label={t('cvm:personScope.persons')}
                value={`${number.format(scope.persons)} (${percent.format(scope.person_share)})`}
              />
              <Figure
                label={t('cvm:personScope.companies')}
                value={number.format(scope.companies)}
              />
              <Figure label={t('cvm:personScope.unknown')} value={number.format(scope.unknown)} />
            </dl>
          </div>
        )}
      </Answer>
    </section>
  );
}

/** A labelled figure, optionally a link into a list. */
export function Figure({
  label,
  value,
  href,
  hint,
}: {
  label: string;
  value: string;
  href?: string;
  hint?: string;
}) {
  return (
    <div className="rounded-md border bg-muted/30 p-3">
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-semibold tabular-nums text-foreground">
        {href ? (
          <Link to={href} className="text-primary underline-offset-2 hover:underline">
            {value}
          </Link>
        ) : (
          value
        )}
      </div>
      {hint ? <div className="text-xs text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

export function Select({
  id,
  label,
  any,
  value,
  onChange,
  options,
  render,
}: {
  id: string;
  label: string;
  any: string;
  value: string;
  onChange: (value: string) => void;
  options?: CvmFilterOption[];
  render?: (option: CvmFilterOption) => string;
}) {
  const { i18n } = useTranslation();
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <div className="space-y-1">
      <label htmlFor={id} className="block text-xs font-medium text-foreground">
        {label}
      </label>
      <select
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        className="max-w-[14rem] rounded-md border bg-background px-2 py-1.5 text-sm text-foreground"
      >
        <option value="">{any}</option>
        {(options ?? []).map((option) => (
          <option key={option.value} value={option.value}>
            {render ? render(option) : option.value} ({number.format(option.count)})
          </option>
        ))}
      </select>
    </div>
  );
}

/** The page footer of every infinite list: the button, or the "all loaded" line. */
export function LoadMore({
  hasNextPage,
  isFetchingNextPage,
  loadMore,
  total,
  pageSize,
}: {
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  loadMore: () => void;
  total: number;
  pageSize: number;
}) {
  const { t, i18n } = useTranslation(['cvm']);
  const number = new Intl.NumberFormat(i18n.language);
  return (
    <div className="flex justify-center py-3">
      {hasNextPage ? (
        <button
          type="button"
          onClick={loadMore}
          disabled={isFetchingNextPage}
          className="rounded-md border bg-background px-3 py-1.5 text-sm text-foreground disabled:opacity-40"
        >
          {isFetchingNextPage
            ? t('cvm:list.loadingMore')
            : t('cvm:list.loadMore', { count: pageSize })}
        </button>
      ) : (
        <p className="text-xs text-muted-foreground">
          {t('cvm:list.allLoaded', { total: number.format(total) })}
        </p>
      )}
    </div>
  );
}

/** Money arrives as a decimal string; a null is a row whose clause states no amount. */
export function useMoney() {
  const { i18n } = useTranslation();
  const currency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'BRL',
  });
  return (value: string | number | null | undefined) =>
    value == null || value === '' ? '—' : currency.format(Number(value));
}

/** The case detail link: the key is a NUP's digits or a legacy `RJ2010/8588`, path-safe. */
export function caseHref(key: string): string {
  return `/cvm/c/${encodeURIComponent(key)}`;
}

export function partyHref(slug: string | null): string | null {
  return slug ? `/cvm/parties/${encodeURIComponent(slug)}` : null;
}

export function directorHref(slug: string | null): string | null {
  return slug ? `/cvm/r/${encodeURIComponent(slug)}` : null;
}

/** A name that links to its record when the source resolved it, plain text otherwise. */
export function NameLink({ name, href }: { name: string | null; href: string | null }) {
  if (!name) return <span className="text-muted-foreground">—</span>;
  if (!href) return <span className="text-foreground">{name}</span>;
  return (
    <Link to={href} className="text-primary underline-offset-2 hover:underline">
      {name}
    </Link>
  );
}

/** The table-scroll region every list shares: sticky opaque header, own scroll box. */
export const TABLE_REGION = 'max-h-[65vh] overflow-auto rounded-lg border';
export const THEAD = 'sticky top-0 z-10 bg-muted text-left text-xs text-muted-foreground';
export const TH = 'px-3 py-2 font-medium';
export const TH_NUM = 'px-3 py-2 text-right font-medium';
export const TD = 'px-3 py-2 text-foreground';
export const TD_NUM = 'px-3 py-2 text-right tabular-nums text-foreground';
export const TD_MUTED = 'px-3 py-2 text-xs text-muted-foreground';
